import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { MenuDotsButton } from '../UI';
import { ContextMenu, type ContextMenuItem } from '../ContextMenu/ContextMenu';
import styles from './InterventionsList.module.css';

interface InterventionRowActionsProps {
  interventionName: string;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

// The row's edit, duplicate and delete actions, behind one menu.
//
// A menu rather than a row of icons (REH-323): treatment plan templates already
// duplicate from a menu, so the two duplicable objects in the product behave alike,
// and there is no copy icon in either icon set to add a third button with.
//
// ContextMenu is portalled, so the panel escapes the table's own overflow instead of
// being clipped by it, and it brings Escape, arrow-key navigation, focus restore and
// the menu/menuitem roles.
export const InterventionRowActions = ({
  interventionName,
  onEdit,
  onDuplicate,
  onDelete
}: InterventionRowActionsProps) => {
  const { t } = useTranslation();
  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null);

  const items = useMemo<ContextMenuItem[]>(
    () => [
      { label: t('edit'), onClick: onEdit },
      { label: t('duplicate'), onClick: onDuplicate },
      { label: t('delete'), onClick: onDelete, isDestructive: true }
    ],
    [t, onEdit, onDuplicate, onDelete]
  );

  return (
    <div className={styles.rowActions}>
      <MenuDotsButton
        // Names the row as well as the action, so a table of twenty rows does not
        // announce twenty identical "Row actions" buttons. Same reasoning as the
        // location table's row actions.
        ariaLabel={t('config-iv-row-actions-aria', { name: interventionName })}
        isExpanded={menuAnchorRect !== null}
        hasMenuPopup
        onClick={(e) => {
          setMenuAnchorRect(e.currentTarget.getBoundingClientRect());
        }}
      />
      {menuAnchorRect &&
        createPortal(
          <ContextMenu
            anchorRect={menuAnchorRect}
            items={items}
            onClose={() => {
              setMenuAnchorRect(null);
            }}
          />,
          document.body
        )}
    </div>
  );
};
